import React, { useRef, useEffect, useImperativeHandle, forwardRef, useMemo } from 'react';
import cytoscape, { type Core, type EventObject } from 'cytoscape';
import { useTheme } from '@fti/design-system';

// @ts-ignore — cytoscape-cose-bilkent doesn't have types
import coseBilkent from 'cytoscape-cose-bilkent';
// @ts-ignore — cytoscape-fcose doesn't ship types
import fcose from 'cytoscape-fcose';
// @ts-ignore — cytoscape-d3-force doesn't ship types
import d3Force from 'cytoscape-d3-force';

let registered = false;
if (!registered) {
  try { cytoscape.use(coseBilkent); } catch { /* already registered */ }
  try { cytoscape.use(fcose); } catch { /* already registered */ }
  try { cytoscape.use(d3Force); } catch { /* already registered */ }
  registered = true;
}

import { buildNodeColorMap, getNodeColor } from '../../utils/graphColors';
import { runForceLayoutInWorker, type ForceLayoutController } from '../../workers/forceLayoutClient';
import type { WorkerLayoutParams } from '../../workers/forceLayout.worker';

function buildColorMap(elements: CytoscapeElement[], attribute: string, group: 'nodes' | 'edges' = 'nodes'): Map<string, string> {
  const values: string[] = [];
  elements.forEach((el) => {
    if (el.group === group) {
      const val = (el.data[attribute] as string) || 'N/A';
      if (!values.includes(val)) values.push(val);
    }
  });
  return buildNodeColorMap(values);
}

/**
 * Pick cose-bilkent parameters that scale with graph size.
 *
 * Small graphs (<100 nodes) get tight spacing and many iterations for polish;
 * large graphs (>1000 nodes) get wider spacing and fewer iterations so the
 * layout finishes in a few seconds without collapsing into a hairball.
 */
function scaledLayoutOpts(nodeCount: number, overrideRepulsion?: number) {
  const n = Math.max(nodeCount, 1);
  const scale = Math.sqrt(n);
  // idealEdgeLength: ~60 for 20 nodes, ~200 for 500, ~400 for 2000.
  const idealEdgeLength = Math.round(Math.min(500, Math.max(60, 12 * scale)));
  // repulsion: ~300k for 100 nodes, ~2M for 500, ~9M for 2000. Capped so the
  // simulation doesn't explode beyond visual usefulness.
  const repulsion = overrideRepulsion ?? Math.round(Math.min(12_000_000, Math.max(100_000, 4500 * n)));
  // Fewer iterations on big graphs; cose-bilkent's marginal quality past 1500
  // iterations on a 2k graph is negligible.
  const numIter = n > 1500 ? 1200 : n > 500 ? 1800 : 2500;
  return {
    nodeRepulsion: repulsion,
    idealEdgeLength,
    numIter,
    gravity: 0.08,
    gravityRange: 3.5,
    gravityCompound: 1.0,
    gravityRangeCompound: 1.5,
    tilingPaddingVertical: 10,
    tilingPaddingHorizontal: 10,
    randomize: true,
    initialEnergyOnIncremental: 0.3,
    nodeDimensionsIncludeLabels: true,
  };
}

interface CytoscapeElement {
  data: Record<string, unknown>;
  group: 'nodes' | 'edges';
}

export type ColorByAttribute = '_entityType' | '_jurisdiction';

export interface GraphCanvasHandle {
  runLayoutWithOptions: (opts: Record<string, unknown>) => void;
  runWorkerLayout: (params: import('../../workers/forceLayout.worker').WorkerLayoutParams) => void;
  stopLayout: () => void;
  getVisibleIds: () => { nodeIds: string[]; edgeIds: string[] };
  searchNodes: (query: string) => void;
  setEdgeTypeVisibility: (type: string, visible: boolean) => void;
  getSelectedNodeIds: () => string[];
}

interface GraphCanvasProps {
  elements: CytoscapeElement[];
  highlightedIds: Set<string>;
  colorBy: ColorByAttribute;
  edgeFilterKey?: string | null;
  edgeFilterValue?: string;
  onLayoutStart?: () => void;
  onLayoutStop?: () => void;
  onNodeSelect: (elementId: string) => void;
  onEdgeSelect: (elementId: string) => void;
  style?: React.CSSProperties;
}

export const GraphCanvas = forwardRef<GraphCanvasHandle, GraphCanvasProps>(({
  elements,
  highlightedIds,
  colorBy,
  edgeFilterKey,
  edgeFilterValue,
  onLayoutStart,
  onLayoutStop,
  onNodeSelect,
  onEdgeSelect,
  style,
}, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const cyRef = useRef<Core | null>(null);
  const { theme } = useTheme();

  const mutedColor = theme.colors.text.secondary;
  const bgColor = theme.colors.background.default;
  const textColor = theme.colors.text.primary;
  const neutralEdgeColor = theme.colors.border.default;

  // Initialize Cytoscape
  useEffect(() => {
    if (!containerRef.current) return;

    const cy = cytoscape({
      container: containerRef.current,
      elements: [],
      style: [
        {
          selector: 'node',
          style: {
            'background-color': 'data(_color)',
            'label': 'data(label)',
            'color': textColor,
            'font-size': '9px',
            'text-valign': 'bottom',
            'text-margin-y': 4,
            'text-max-width': '80px',
            'text-wrap': 'ellipsis',
            'width': 28,
            'height': 28,
          },
        },
        {
          selector: 'edge',
          style: {
            'width': 1.5,
            'line-color': 'data(_edgeColor)',
            'target-arrow-color': 'data(_edgeColor)',
            'target-arrow-shape': 'triangle',
            'curve-style': 'bezier',
            'label': 'data(label)',
            'font-size': '7px',
            'color': mutedColor,
            'text-rotation': 'autorotate',
          },
        },
        {
          selector: '.highlighted',
          style: {
            'opacity': 1,
          },
        },
        {
          selector: '.dimmed',
          style: {
            'opacity': 0.15,
          },
        },
        {
          selector: ':selected',
          style: {
            'border-width': 3,
            'border-color': theme.colors.semantic.warning.default,
          },
        },
      ],
      // No elements yet — a concrete layout runs in the element-load effect
      // below once nodes/edges are added and we can size parameters by count.
      layout: { name: 'preset' } as any,
      minZoom: 0.1,
      maxZoom: 5,
    });

    cy.on('tap', 'node', (evt: EventObject) => {
      const id = evt.target.data('_element_id') || evt.target.id();
      onNodeSelect(id);
    });

    cy.on('tap', 'edge', (evt: EventObject) => {
      const id = evt.target.data('_element_id') || evt.target.id();
      onEdgeSelect(id);
    });

    cyRef.current = cy;

    return () => {
      cy.destroy();
      cyRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Expose runLayout for Play button — uses current positions as starting
  // points so the graph expands/contracts in place rather than flipping 180 degrees.
  // Only includes visible edges in physics so edge-filtered nodes aren't pulled together.
  // Track current layout so we can cancel it on re-run
  const currentLayoutRef = useRef<any>(null);
  const currentWorkerLayoutRef = useRef<ForceLayoutController | null>(null);

  // Persist node positions by id across element swaps. Switching to a Cypher
  // subgraph and back, or clearing the subgraph, rebuilds `elements` from a
  // different hook state — but nodes that reappear should keep where the
  // user left them. Clearing this ref is only done on unmount.
  const nodePositionCacheRef = useRef<Map<string, { x: number; y: number }>>(new Map());

  // Stable refs for start/stop callbacks so the imperative handle doesn't
  // need to re-bind on every render.
  const onLayoutStartRef = useRef(onLayoutStart);
  onLayoutStartRef.current = onLayoutStart;
  const onLayoutStopRef = useRef(onLayoutStop);
  onLayoutStopRef.current = onLayoutStop;

  useImperativeHandle(ref, () => ({
    runLayoutWithOptions: (opts: Record<string, unknown>) => {
      const cy = cyRef.current;
      if (!cy) return;

      // Cancel any in-progress layout
      if (currentLayoutRef.current) {
        try { currentLayoutRef.current.stop(); } catch { /* ignore */ }
        currentLayoutRef.current = null;
      }

      // Build element collection: all nodes + only visible edges.
      // This keeps edge-filtered nodes from being pulled together.
      const visibleEdges = cy.edges().filter((edge) => edge.style('display') !== 'none');
      const layoutElements = cy.nodes().union(visibleEdges);

      const layout = layoutElements.layout(opts as any);
      currentLayoutRef.current = layout;

      // Fire start/stop callbacks so the host app can update UI state (Play
      // button toggle, spinner, etc.) without polling. The `layoutstop` event
      // is fired by Cytoscape on both natural completion and manual .stop().
      layout.on('layoutstart', () => { onLayoutStartRef.current?.(); });
      layout.on('layoutstop', () => {
        if (currentLayoutRef.current === layout) currentLayoutRef.current = null;
        onLayoutStopRef.current?.();
      });

      // Yield to the browser before kicking off so the calling click handler
      // returns and the UI re-paints before layout math begins. This doesn't
      // offload the work (Cytoscape layouts are main-thread), but per-frame
      // `animate: true` ticks give the browser input-handling windows
      // between iterations so the rest of the app stays responsive.
      requestAnimationFrame(() => {
        try { layout.run(); } catch { /* layout may have been canceled */ }
      });
    },
    runWorkerLayout: (params: WorkerLayoutParams) => {
      const cy = cyRef.current;
      if (!cy) return;

      // Tear down any running Cytoscape-native layout or prior worker.
      if (currentLayoutRef.current) {
        try { currentLayoutRef.current.stop(); } catch { /* ignore */ }
        currentLayoutRef.current = null;
      }
      if (currentWorkerLayoutRef.current) {
        currentWorkerLayoutRef.current.stop();
        currentWorkerLayoutRef.current = null;
      }

      // Fire onStart synchronously so the Play button flips state before
      // the worker boots (worker spin-up can take ~30–50ms).
      onLayoutStartRef.current?.();

      // Defer one frame so the UI can paint the button state change
      // before we hand off to the worker.
      requestAnimationFrame(() => {
        const controller = runForceLayoutInWorker(cy, {
          params,
          // onStart is already fired above; swallow the worker's first-tick
          // signal to avoid a double-fire.
          onStart: () => { /* noop */ },
          onStop: () => {
            if (currentWorkerLayoutRef.current === controller) {
              currentWorkerLayoutRef.current = null;
            }
            onLayoutStopRef.current?.();
          },
        });
        currentWorkerLayoutRef.current = controller;
      });
    },
    stopLayout: () => {
      if (currentLayoutRef.current) {
        try { currentLayoutRef.current.stop(); } catch { /* ignore */ }
        currentLayoutRef.current = null;
        onLayoutStopRef.current?.();
      }
      if (currentWorkerLayoutRef.current) {
        currentWorkerLayoutRef.current.stop();
        currentWorkerLayoutRef.current = null;
      }
    },
    getVisibleIds: () => {
      const cy = cyRef.current;
      if (!cy) return { nodeIds: [], edgeIds: [] };
      const visibleNodes = cy.nodes().filter((n) => n.style('display') !== 'none');
      const visibleEdges = cy.edges().filter((e) => e.style('display') !== 'none');
      return {
        nodeIds: visibleNodes.map((n) => n.id()),
        edgeIds: visibleEdges.map((e) => e.id()),
      };
    },
    searchNodes: (query: string) => {
      const cy = cyRef.current;
      if (!cy) return;
      // Reset all node borders
      cy.nodes().style({ 'border-width': 0, 'border-color': '' });
      if (!query.trim()) return;
      const q = query.toLowerCase();
      const matches = cy.nodes().filter((n) => {
        const name = (n.data('_name') || n.data('label') || n.data('id') || '').toString().toLowerCase();
        if (name.includes(q)) return true;
        // Search all property values
        const data = n.data();
        for (const key of Object.keys(data)) {
          if (key.startsWith('_') || key === 'id' || key === 'label') continue;
          const val = data[key];
          if (val == null) continue;
          const str = Array.isArray(val) ? val.join(' ') : String(val);
          if (str.toLowerCase().includes(q)) return true;
        }
        return false;
      });
      if (matches.length > 0) {
        matches.style({ 'border-width': 4, 'border-color': '#ff6600' });
        cy.fit(matches, 50);
      }
    },
    getSelectedNodeIds: () => {
      // Union of :selected (box-select / tap-select) and .highlighted (search-hit).
      // Either mechanism counts as "the nodes the user wants exported" — whichever
      // UX they used to express the selection.
      const cy = cyRef.current;
      if (!cy) return [];
      const ids = new Set<string>();
      (cy.$(':selected').nodes() as any).forEach((n: any) => ids.add(n.id()));
      (cy.nodes('.highlighted') as any).forEach((n: any) => ids.add(n.id()));
      return Array.from(ids);
    },
    setEdgeTypeVisibility: (type: string, visible: boolean) => {
      const cy = cyRef.current;
      if (!cy) return;
      const edges = cy.edges().filter((e) => e.data('_type') === type) as any;
      if (visible) {
        edges.show();
      } else {
        edges.hide();
      }
    },
  }), []);

  // Load elements into Cytoscape — only fires when elements reference changes (memoized in useGraph)
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy || elements.length === 0) return;

    // Defensive orphan-edge filter. Any edge whose source or target id
    // is not in the current node set would throw inside Cytoscape's add()
    // and crash the canvas through React's ErrorBoundary. Drop them and
    // warn so we can diagnose upstream data inconsistencies.
    const nodeIds = new Set<string>();
    for (const el of elements) {
      if (el.group === 'nodes') {
        const id = el.data.id;
        if (typeof id === 'string') nodeIds.add(id);
      }
    }
    const orphans: typeof elements = [];
    const safeElements = elements.filter((el) => {
      if (el.group !== 'edges') return true;
      const src = el.data.source;
      const tgt = el.data.target;
      const ok = typeof src === 'string' && typeof tgt === 'string'
        && nodeIds.has(src) && nodeIds.has(tgt);
      if (!ok) orphans.push(el);
      return ok;
    });
    if (orphans.length > 0) {
      console.warn(
        `[GraphCanvas] Dropped ${orphans.length} orphan edge(s); endpoints missing from node set.`,
        orphans.slice(0, 5).map((e) => ({
          id: e.data.id,
          source: e.data.source,
          target: e.data.target,
          label: e.data.label,
        })),
      );
    }

    const nodeColorMap = buildColorMap(safeElements, colorBy);
    const edgeTypeColorMap = buildColorMap(safeElements, '_type', 'edges');

    // Snapshot current positions into the cache before clearing, so nodes that
    // reappear after a Cypher-subgraph swap or a Clear Cypher land back where
    // the user last saw them.
    cy.nodes().forEach((n) => {
      const pos = n.position();
      if (pos && Number.isFinite(pos.x) && Number.isFinite(pos.y)) {
        nodePositionCacheRef.current.set(n.id(), { x: pos.x, y: pos.y });
      }
    });

    cy.elements().remove();

    // Seed cached positions onto the rendered elements so Cytoscape honors them
    // when it adds the nodes. Tracks which ids need a fresh layout run.
    const newNodeIds: string[] = [];
    const colored = safeElements.map((el) => {
      if (el.group === 'nodes') {
        const val = (el.data[colorBy] as string) || 'Unknown';
        const id = typeof el.data.id === 'string' ? el.data.id : undefined;
        const cached = id ? nodePositionCacheRef.current.get(id) : undefined;
        if (!cached && id) newNodeIds.push(id);
        return {
          ...el,
          data: { ...el.data, _color: nodeColorMap.get(val) || getNodeColor(val) },
          ...(cached ? { position: { x: cached.x, y: cached.y } } : {}),
        };
      }
      if (el.group === 'edges') {
        const type = (el.data._type as string) || 'Unknown';
        return { ...el, data: { ...el.data, _edgeColor: edgeTypeColorMap.get(type) || neutralEdgeColor } };
      }
      return el;
    });
    cy.add(colored as any);

    if (currentWorkerLayoutRef.current) {
      currentWorkerLayoutRef.current.stop();
      currentWorkerLayoutRef.current = null;
    }

    // If every node had a cached position, skip the initial force layout — the
    // user has seen these nodes before and we want them to stay put. This is the
    // common case when toggling between the main graph and a cypher subgraph.
    if (newNodeIds.length === 0) {
      cy.fit(undefined, 40);
      return;
    }

    // Otherwise, run the d3-force worker so new nodes get sensible positions.
    // Worker layouts (cose-bilkent / fcose / d3-force) block the main thread;
    // the web worker off-loads the physics entirely. `infinite:false` lets the
    // sim converge and stop on its own.
    onLayoutStartRef.current?.();
    requestAnimationFrame(() => {
      const controller = runForceLayoutInWorker(cy, {
        params: {
          linkDistance: 400,
          linkStrength: 0.5,
          manyBodyStrength: -750,
          centerStrength: 0.1,
          collideRadius: 30,
          collideStrength: 0.9,
          alpha: 1,
          alphaDecay: 0.0228,
          velocityDecay: 0.4,
          infinite: false,
        },
        onStart: () => { /* already fired above */ },
        onStop: () => {
          if (currentWorkerLayoutRef.current === controller) {
            currentWorkerLayoutRef.current = null;
          }
          // Snapshot settled positions into the cache so the next element swap
          // can honor them.
          cy.nodes().forEach((n) => {
            const pos = n.position();
            if (pos && Number.isFinite(pos.x) && Number.isFinite(pos.y)) {
              nodePositionCacheRef.current.set(n.id(), { x: pos.x, y: pos.y });
            }
          });
          onLayoutStopRef.current?.();
        },
      });
      currentWorkerLayoutRef.current = controller;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elements]); // Only re-layout when the actual graph data changes (stable ref from useMemo)

  // Node color-only updates — fires when colorBy changes but elements haven't
  const prevColorByRef = useRef(colorBy);
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy || elements.length === 0) return;
    if (prevColorByRef.current === colorBy) return;
    prevColorByRef.current = colorBy;

    const colorMap = buildColorMap(elements, colorBy);
    cy.nodes().forEach((node) => {
      const val = (node.data(colorBy) as string) || 'Unknown';
      node.style('background-color', colorMap.get(val) || getNodeColor(val));
    });
  }, [colorBy, elements]);

  // Update highlighting
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;

    cy.elements().removeClass('highlighted dimmed');

    if (highlightedIds.size > 0) {
      cy.elements().forEach((ele) => {
        const id = ele.data('_element_id') || ele.id();
        if (highlightedIds.has(id)) {
          ele.addClass('highlighted');
        } else {
          ele.addClass('dimmed');
        }
      });
    }
  }, [highlightedIds]);

  // Edge property filter — also hides nodes with no visible edges
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;

    if (!edgeFilterKey || !edgeFilterValue) {
      // Clear filter — show all edges and nodes
      cy.edges().removeClass('dimmed').style('display', 'element');
      cy.nodes().style('display', 'element');
      return;
    }

    const filterVal = edgeFilterValue.toLowerCase();
    const connectedNodeIds = new Set<string>();

    cy.edges().forEach((edge) => {
      const val = String(edge.data(edgeFilterKey) ?? '').toLowerCase();
      if (val === filterVal) {
        edge.style('display', 'element');
        edge.removeClass('dimmed');
        connectedNodeIds.add(edge.source().id());
        connectedNodeIds.add(edge.target().id());
      } else {
        edge.style('display', 'none');
      }
    });

    // Hide nodes that have no visible edges
    cy.nodes().forEach((node) => {
      if (connectedNodeIds.has(node.id())) {
        node.style('display', 'element');
      } else {
        node.style('display', 'none');
      }
    });
  }, [edgeFilterKey, edgeFilterValue]);

  // Build node legend
  const nodeColorMap = buildColorMap(elements, colorBy);
  const nodeLegendEntries = Array.from(nodeColorMap.entries()).slice(0, 15);

  // Edge legend — derived from the edge-type color map so the entries match
  // the colors drawn on the canvas. Under amaryllis, edge types are the
  // SCREAMING_SNAKE_CASE relationship labels (AUTHORED_BY, OWNS, DONATED_TO, …).
  const edgeLegendEntries = useMemo<[string, string][]>(() => {
    const map = buildColorMap(elements, '_type', 'edges');
    return Array.from(map.entries()).slice(0, 15);
  }, [elements]);

  const containerHeight = style?.height || 'calc(100vh - 230px)';

  return (
    <div style={{ position: 'relative', height: style?.height ? '100%' : undefined }}>
      <div
        ref={containerRef}
        style={{
          width: '100%',
          height: containerHeight,
          background: bgColor,
          border: `1px solid ${theme.colors.border.default}`,
          borderRadius: '4px',
          overflow: 'hidden',
          position: 'relative',
          minWidth: 0,
        }}
      />
      {/* Legends */}
      <div style={{
        position: 'absolute',
        bottom: 8,
        left: 8,
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        maxHeight: 'calc(100% - 16px)',
        overflow: 'auto',
      }}>
        {/* Node Legend */}
        {nodeLegendEntries.length > 0 && (
          <div style={{
            background: `${theme.colors.background.elevated}ee`,
            border: `1px solid ${theme.colors.border.default}`,
            borderRadius: '4px',
            padding: '6px 10px',
            fontSize: '0.7rem',
            fontFamily: "'Source Sans 3', sans-serif",
            maxWidth: '200px',
          }}>
            <div style={{ fontWeight: 600, marginBottom: '4px', color: textColor }}>
              {colorBy === '_entityType' ? 'Entity Type' : 'Jurisdiction'}
            </div>
            {nodeLegendEntries.map(([name, color]) => (
              <div key={name} style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
                <span style={{ color: textColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
              </div>
            ))}
          </div>
        )}
        {/* Edge Legend */}
        {edgeLegendEntries.length > 0 && (
          <div style={{
            background: `${theme.colors.background.elevated}ee`,
            border: `1px solid ${theme.colors.border.default}`,
            borderRadius: '4px',
            padding: '6px 10px',
            fontSize: '0.7rem',
            fontFamily: "'Source Sans 3', sans-serif",
            maxWidth: '200px',
          }}>
            <div style={{ fontWeight: 600, marginBottom: '4px', color: textColor }}>
              Edge Type
            </div>
            {edgeLegendEntries.map(([name, color]) => (
              <div key={name} style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
                <div style={{ width: 10, height: 4, borderRadius: '2px', background: color, flexShrink: 0 }} />
                <span style={{ color: textColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});
